/**
 * Service-area editor kernel (D616) - pure.
 *
 * The service area of a market is the union of its ACTIVE `operation` fences (`evaluateServiceArea`). Until D616 the only way
 * to change it was a seed; this module is everything an Admin editor and its four-eyes publication need that is NOT I/O: the
 * limits and the stable findings that decide whether a drawing is publishable, normalization (so the same drawing always hashes
 * the same), area and perimeter, circle-to-polygon (so the pilot's circular area can be the first draft), GeoJSON/KML import
 * and GeoJSON export, and the pure construction of the NEXT WHOLE fence set when a proposal is approved.
 *
 * What a drawing may be: a polygon or a multipolygon, each ring a simple closed loop WITHOUT holes (the geofence kernel has no
 * holes, so a ring inside another ring is refused rather than silently read as a hole). Every refusal is a stable
 * `SERVICE_AREA_*` code with the 0-based ring it concerns - the editor shows plain language from the vocabulary catalog, never
 * the code. Findings are reported for the whole drawing at once (an incomplete drawing may be saved; only a clean one is
 * submitted), and an area/centre judgement is withheld while the geometry is still structurally broken, so a bow-tie never also
 * reports a nonsense "too small".
 *
 * Geometry method. Intersection tests (self-intersection, ring overlap) run in the lon/lat plane with exact orientation tests and
 * collinear-overlap handling - topology does not depend on the projection, and touching counts as intersecting (fail closed).
 * Area is the shoelace formula on a local equirectangular projection around each ring's bounding-box centre (metres per degree
 * from the shared Earth radius, longitude scaled by cos(latitude)); at the scale a service area is allowed to have (at most
 * 25 000 km2, a ring about 160 km across) the error is well under 1 %. Perimeter is the haversine length of the closed ring.
 * Rings never overlap (checked), so the area of the union is the sum of the rings.
 *
 * Publication. `nextServiceAreaFences` builds the next whole set: non-`operation` fences ride along untouched (a `block` fence is
 * never edited by an area change), and the new operation fence never starts before the approval instant - a ride that already
 * snapshotted `{ fenceSetVersion, contentHash }` must stay true. The old area is NEVER dropped while it is in force: it is
 * carried clipped to end exactly when the new one starts (also for an immediate publication), so a replica whose clock lags the
 * approval by a few seconds still sees an area instead of "not declared" (D616 round 2, clock skew). Only fences that ended more
 * than {@link SERVICE_AREA_SKEW_GRACE_MS} before the approval are pruned.
 *
 * Operating hours (D616 round 2). A service area may carry a {@link Schedule} (local to the market timezone): outside its windows
 * the area is closed and `evaluateServiceArea` refuses every point with `SERVICE_AREA_CLOSED`. {@link serviceAreaScheduleFindings}
 * judges one, {@link overnightWindows} writes a window that crosses midnight, {@link nextServiceAreaOpening} says when it opens.
 *
 * Neutral: no country, currency, language or unit other than metres is named here. No I/O, no clock.
 */
import { EARTH_RADIUS_METERS, haversineMeters, isLatLng } from '@vello/geo';
import { contains, validateGeometry } from './geometry.js';
import { isEffectiveAt } from './geofence.js';
import { isActiveAt } from './evaluate.js';
import { evaluateServiceArea } from './service-area.js';
import { isValidTimeZone, validateSchedule } from './schedule.js';
/** The editor's bounds. Areas are in square metres: 10 000 m2 (1 ha) .. 25 000 km2 - a market, not a continent. */
export const SERVICE_AREA_LIMITS = { maxRings: 20, maxVerticesPerRing: 1000, maxVertices: 5000, minAreaSquareMeters: 10_000, maxAreaSquareMeters: 25_000_000_000 };
/** The stable findings of a drawing; each has a plain-language text in the vocabulary catalog (`geofence.service_area_issue`). */
export const SERVICE_AREA_ISSUES = [
    'SERVICE_AREA_SHAPE_UNSUPPORTED',
    'SERVICE_AREA_EMPTY',
    'SERVICE_AREA_TOO_MANY_RINGS',
    'SERVICE_AREA_TOO_FEW_VERTICES',
    'SERVICE_AREA_TOO_MANY_VERTICES',
    'SERVICE_AREA_INVALID_COORDINATE',
    'SERVICE_AREA_REPEATED_VERTEX',
    'SERVICE_AREA_SELF_INTERSECTION',
    'SERVICE_AREA_RINGS_OVERLAP',
    'SERVICE_AREA_CROSSES_ANTIMERIDIAN',
    'SERVICE_AREA_TOO_SMALL',
    'SERVICE_AREA_TOO_LARGE',
    'SERVICE_AREA_CENTER_OUTSIDE',
    'SERVICE_AREA_CENTER_UNKNOWN',
    'SERVICE_AREA_SCHEDULE_INVALID',
    'SERVICE_AREA_SCHEDULE_TOO_MANY_WINDOWS',
    'SERVICE_AREA_SCHEDULE_EMPTY',
];
/** Operating hours bounds: at most this many windows (a week with a lunch break on every day is 14). */
export const SERVICE_AREA_SCHEDULE_LIMITS = { maxWindows: 50 };
/** How far before the approval an ended operation fence is still kept (clock skew between replicas), in milliseconds: 10 minutes. */
export const SERVICE_AREA_SKEW_GRACE_MS = 10 * 60 * 1000;
const DEGREES = Math.PI / 180;
const METERS_PER_DEGREE = EARTH_RADIUS_METERS * DEGREES;
const COORDINATE_DECIMALS = 1e7;
const same = (a, b) => a.latitude === b.latitude && a.longitude === b.longitude;
/** The rings of a service-area geometry as given (polygon -> [ring]). */
export function serviceAreaRings(geometry) {
    return geometry.kind === 'polygon' ? [geometry.ring] : geometry.polygons;
}
/** A ring without its closing duplicate vertex (the last vertex equal to the first). */
function open(ring) {
    return ring.length > 1 && same(ring[0], ring[ring.length - 1]) ? ring.slice(0, -1) : ring;
}
/** The ring with consecutive equal vertices (including last == first) collapsed - what the intersection tests walk. */
function collapsed(ring) {
    const out = [];
    for (const v of ring)
        if (out.length === 0 || !same(out[out.length - 1], v))
            out.push(v);
    while (out.length > 1 && same(out[0], out[out.length - 1]))
        out.pop();
    return out;
}
const round7 = (n) => Math.round(n * COORDINATE_DECIMALS) / COORDINATE_DECIMALS + 0; // `+ 0` turns -0 into 0
/** Normalize: drop each ring's closing duplicate vertex, round to 7 decimals (~1 cm); a single-ring multipolygon becomes a polygon. Pure. */
export function normalizeServiceArea(geometry) {
    const rings = serviceAreaRings(geometry).map((ring) => open(ring.map((v) => ({ latitude: round7(v.latitude), longitude: round7(v.longitude) }))));
    return rings.length === 1 ? { kind: 'polygon', ring: rings[0] } : { kind: 'multipolygon', polygons: rings };
}
/** Twice the signed area of the triangle a-b-c in the lon/lat plane (sign = turn direction; 0 = collinear). */
function orient(a, b, c) {
    return (b.longitude - a.longitude) * (c.latitude - a.latitude) - (b.latitude - a.latitude) * (c.longitude - a.longitude);
}
/** Whether `p` (known collinear with a-b) lies within the segment's bounding box. */
function onSegment(a, b, p) {
    return Math.min(a.longitude, b.longitude) <= p.longitude && p.longitude <= Math.max(a.longitude, b.longitude) && Math.min(a.latitude, b.latitude) <= p.latitude && p.latitude <= Math.max(a.latitude, b.latitude);
}
/** Closed-segment intersection (proper crossing, touching, or collinear overlap) in the lon/lat plane. */
function segmentsIntersect(p1, p2, p3, p4) {
    const d1 = orient(p3, p4, p1);
    const d2 = orient(p3, p4, p2);
    const d3 = orient(p1, p2, p3);
    const d4 = orient(p1, p2, p4);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0)))
        return true;
    return (d1 === 0 && onSegment(p3, p4, p1)) || (d2 === 0 && onSegment(p3, p4, p2)) || (d3 === 0 && onSegment(p1, p2, p3)) || (d4 === 0 && onSegment(p1, p2, p4));
}
/** Whether a simple-ring candidate touches itself: non-adjacent edges meet, or two adjacent edges fold back over each other. */
function selfIntersects(ring) {
    const n = ring.length;
    for (let i = 0; i < n; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % n];
        const c = ring[(i + 2) % n];
        // adjacent edges a-b and b-c: collinear AND heading back over each other = a spike
        if (orient(a, b, c) === 0 && (b.longitude - a.longitude) * (c.longitude - b.longitude) + (b.latitude - a.latitude) * (c.latitude - b.latitude) < 0)
            return true;
        for (let j = i + 2; j < n; j++) {
            if (i === 0 && j === n - 1)
                continue; // the closing edge is adjacent to the first
            if (segmentsIntersect(a, b, ring[j], ring[(j + 1) % n]))
                return true;
        }
    }
    return false;
}
function boxOf(ring) {
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    for (const v of ring) {
        if (v.latitude < minLat)
            minLat = v.latitude;
        if (v.latitude > maxLat)
            maxLat = v.latitude;
        if (v.longitude < minLng)
            minLng = v.longitude;
        if (v.longitude > maxLng)
            maxLng = v.longitude;
    }
    return { minLat, maxLat, minLng, maxLng };
}
const boxesTouch = (a, b) => a.minLat <= b.maxLat && b.minLat <= a.maxLat && a.minLng <= b.maxLng && b.minLng <= a.maxLng;
/** Whether two rings' boundaries meet, or one lies inside the other (holes are unsupported, so nesting is an overlap). */
function ringsOverlap(a, b) {
    for (let i = 0; i < a.length; i++) {
        for (let j = 0; j < b.length; j++) {
            if (segmentsIntersect(a[i], a[(i + 1) % a.length], b[j], b[(j + 1) % b.length]))
                return true;
        }
    }
    // no boundary meets: either disjoint or nested, and one vertex tells which
    return contains({ kind: 'polygon', ring: a }, b[0]) || contains({ kind: 'polygon', ring: b }, a[0]);
}
/** Area of one ring (m2): shoelace on a local equirectangular projection around the ring's bounding-box centre. */
function ringAreaSquareMeters(ring) {
    if (ring.length < 3)
        return 0;
    const box = boxOf(ring);
    const lat0 = (box.minLat + box.maxLat) / 2;
    const lng0 = (box.minLng + box.maxLng) / 2;
    const mPerLng = METERS_PER_DEGREE * Math.cos(lat0 * DEGREES);
    let sum = 0;
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        sum += (a.longitude - lng0) * mPerLng * ((b.latitude - lat0) * METERS_PER_DEGREE) - (b.longitude - lng0) * mPerLng * ((a.latitude - lat0) * METERS_PER_DEGREE);
    }
    return Math.abs(sum) / 2;
}
function ringPerimeterMeters(ring) {
    let sum = 0;
    for (let i = 0; i < ring.length; i++)
        sum += haversineMeters(ring[i], ring[(i + 1) % ring.length]);
    return sum;
}
/** Area (m2, local equirectangular shoelace per ring - see the module header), perimeter (m, haversine), vertex and ring counts. */
export function serviceAreaStats(geometry) {
    const rings = serviceAreaRings(geometry).map(open);
    return {
        areaSquareMeters: rings.reduce((sum, r) => sum + ringAreaSquareMeters(r), 0),
        perimeterMeters: rings.reduce((sum, r) => sum + ringPerimeterMeters(r), 0),
        vertices: rings.reduce((sum, r) => sum + r.length, 0),
        rings: rings.length,
    };
}
/**
 * Every finding of a drawing (empty = publishable), in a stable order (shape, then each ring in order, then whole-drawing vertex
 * count, ring overlaps, area, centre), at most one per (issue, ring). `center` is the market centre: it must lie in the union.
 * The key is REQUIRED so every caller decides: an unknown (or invalid) centre is itself a finding (`SERVICE_AREA_CENTER_UNKNOWN`) -
 * a drawing is never published against a centre nobody could check. Area and centre are judged only when every ring is
 * structurally sound and the geometry is otherwise clean (a draft save looks only at the structural findings).
 */
export function serviceAreaFindings(geometry, opts) {
    if (geometry.kind !== 'polygon' && geometry.kind !== 'multipolygon')
        return [{ issue: 'SERVICE_AREA_SHAPE_UNSUPPORTED' }];
    const raw = serviceAreaRings(geometry);
    if (raw.length === 0)
        return [{ issue: 'SERVICE_AREA_EMPTY' }];
    if (raw.length > SERVICE_AREA_LIMITS.maxRings)
        return [{ issue: 'SERVICE_AREA_TOO_MANY_RINGS' }];
    const findings = [];
    const add = (issue, ring) => {
        findings.push(ring === undefined ? { issue } : { issue, ring });
    };
    const rings = raw.map(open);
    const walkable = []; // the collapsed ring when it can be tested geometrically, else null
    let geometricallyClean = true;
    rings.forEach((ring, index) => {
        let usable = true;
        if (!ring.every(isLatLng)) {
            add('SERVICE_AREA_INVALID_COORDINATE', index);
            usable = false;
        }
        if (ring.length > SERVICE_AREA_LIMITS.maxVerticesPerRing) {
            add('SERVICE_AREA_TOO_MANY_VERTICES', index);
            usable = false;
        }
        if (usable) {
            const distinct = new Set(ring.map((v) => `${v.latitude},${v.longitude}`)).size;
            if (distinct < 3) {
                add('SERVICE_AREA_TOO_FEW_VERTICES', index);
                usable = false;
            }
        }
        else if (ring.length < 3) {
            add('SERVICE_AREA_TOO_FEW_VERTICES', index);
        }
        if (ring.some((v, i) => i > 0 && same(ring[i - 1], v)) || (ring.length > 1 && same(ring[0], ring[ring.length - 1])))
            add('SERVICE_AREA_REPEATED_VERTEX', index);
        if (!usable) {
            walkable.push(null);
            geometricallyClean = false;
            return;
        }
        const walk = collapsed(ring);
        walkable.push(walk);
        const box = boxOf(walk);
        if (box.maxLng - box.minLng > 180) {
            add('SERVICE_AREA_CROSSES_ANTIMERIDIAN', index);
            geometricallyClean = false;
        }
        if (selfIntersects(walk)) {
            add('SERVICE_AREA_SELF_INTERSECTION', index);
            geometricallyClean = false;
        }
    });
    const total = rings.reduce((sum, r) => sum + r.length, 0);
    if (total > SERVICE_AREA_LIMITS.maxVertices) {
        add('SERVICE_AREA_TOO_MANY_VERTICES');
        return findings; // pairwise tests over an oversized drawing are not worth running
    }
    const boxes = walkable.map((w) => (w === null ? null : boxOf(w)));
    const overlapping = new Set();
    for (let j = 1; j < walkable.length; j++) {
        const wj = walkable[j];
        const bj = boxes[j];
        if (wj === null || bj === null)
            continue;
        for (let i = 0; i < j; i++) {
            const wi = walkable[i];
            const bi = boxes[i];
            if (wi === null || bi === null || !boxesTouch(bi, bj))
                continue;
            if (ringsOverlap(wi, wj)) {
                overlapping.add(j);
                break;
            }
        }
    }
    for (const j of overlapping)
        add('SERVICE_AREA_RINGS_OVERLAP', j);
    if (overlapping.size > 0)
        geometricallyClean = false;
    const sound = walkable.every((w) => w !== null);
    if (!sound || !geometricallyClean)
        return findings;
    const area = rings.reduce((sum, r) => sum + ringAreaSquareMeters(r), 0);
    if (area < SERVICE_AREA_LIMITS.minAreaSquareMeters)
        add('SERVICE_AREA_TOO_SMALL');
    else if (area > SERVICE_AREA_LIMITS.maxAreaSquareMeters)
        add('SERVICE_AREA_TOO_LARGE');
    const center = opts.center;
    if (center === undefined || !isLatLng(center))
        add('SERVICE_AREA_CENTER_UNKNOWN');
    else if (!rings.some((r) => contains({ kind: 'polygon', ring: r }, center)))
        add('SERVICE_AREA_CENTER_OUTSIDE');
    return findings;
}
/** Area (m2) of a circle, or of the polygons of a service-area drawing (sum of the rings - they never overlap); 0 for anything else. */
function geometryAreaSquareMeters(geometry) {
    if (geometry.kind === 'circle')
        return Math.PI * geometry.radiusMeters * geometry.radiusMeters;
    if (geometry.kind === 'polygon' || geometry.kind === 'multipolygon')
        return serviceAreaStats(geometry).areaSquareMeters;
    return 0;
}
/**
 * How much a proposed drawing changes the area in force (the shrink guard): `current` = the sum of the areas of the `operation`
 * fences in effect at `at` (schedule ignored - hours do not change the area; a circle is pi r2, a polygon uses the editor's area;
 * fences of one set do not overlap by construction, so the sum is the union), `null` when none is in effect (a first drawing).
 * `changePercent` = (new - current) / current * 100, `null` when there is no current area. `timeZone` is accepted for symmetry with the
 * other evaluators and is not needed because the schedule is ignored.
 */
export function serviceAreaChange(input) {
    const inForce = input.current.filter((f) => f.purpose === 'operation' && validateGeometry(f.geometry) === null && isEffectiveAt(f, input.at));
    const newAreaSquareMeters = serviceAreaStats(input.geometry).areaSquareMeters;
    if (inForce.length === 0)
        return { currentAreaSquareMeters: null, newAreaSquareMeters, changePercent: null };
    const currentAreaSquareMeters = inForce.reduce((sum, f) => sum + geometryAreaSquareMeters(f.geometry), 0);
    return { currentAreaSquareMeters, newAreaSquareMeters, changePercent: currentAreaSquareMeters > 0 ? ((newAreaSquareMeters - currentAreaSquareMeters) / currentAreaSquareMeters) * 100 : null };
}
/** How many of `points` lie outside the drawing (an invalid point counts as outside - never proven inside). */
export function pointsOutside(geometry, points) {
    return points.filter((p) => !isLatLng(p) || !contains(geometry, p)).length;
}
const WEEKDAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
/**
 * Operating hours findings (empty = publishable). `windows` must be a non-empty list of valid windows (days from mon..sun, `HH:MM`
 * start, `HH:MM` or `24:00` end, start before end) of at most {@link SERVICE_AREA_SCHEDULE_LIMITS}.maxWindows. A schedule with NO
 * windows is refused (`SERVICE_AREA_SCHEDULE_EMPTY`): "open 24 hours" is "no schedule", and an empty list written by mistake must
 * not silently mean that. Never throws on a malformed value from the wire.
 */
export function serviceAreaScheduleFindings(schedule) {
    const windows = schedule?.windows;
    if (!Array.isArray(windows))
        return [{ issue: 'SERVICE_AREA_SCHEDULE_INVALID' }];
    if (windows.length === 0)
        return [{ issue: 'SERVICE_AREA_SCHEDULE_EMPTY' }];
    if (windows.length > SERVICE_AREA_SCHEDULE_LIMITS.maxWindows)
        return [{ issue: 'SERVICE_AREA_SCHEDULE_TOO_MANY_WINDOWS' }];
    const shaped = windows.every((w) => {
        if (typeof w !== 'object' || w === null)
            return false;
        const { days, start, end } = w;
        return Array.isArray(days) && days.every((d) => WEEKDAY_ORDER.includes(d)) && typeof start === 'string' && typeof end === 'string';
    });
    if (!shaped || validateSchedule(schedule) !== null)
        return [{ issue: 'SERVICE_AREA_SCHEDULE_INVALID' }];
    return [];
}
/**
 * The windows that express "open `days` from `start` to `end`" when `end` may be on the next day: 22:00-02:00 on [mon] becomes
 * [mon 22:00-24:00] + [tue 00:00-02:00] (a window never crosses midnight in the schedule model). Same-day hours (start before end)
 * come back as one window; `end` at 00:00 means the end of the day (22:00-00:00 = 22:00-24:00). `start === end` is returned as given
 * so the validator refuses it - an editor decides what "24 hours" means, not this function.
 */
export function overnightWindows(days, start, end) {
    if (start === end)
        return [{ days, start, end }];
    if (start < end || end === '24:00')
        return [{ days, start, end }];
    const nextDays = days.map((d) => WEEKDAY_ORDER[(WEEKDAY_ORDER.indexOf(d) + 1) % 7]);
    const first = { days, start, end: '24:00' };
    return end === '00:00' ? [first] : [first, { days: nextDays, start: '00:00', end }];
}
/**
 * A circle as a polygon of `segments` vertices (default 64, clamped to 3..1000), every vertex exactly `radiusMeters` from the centre
 * (great-circle destination points, rounded to 7 decimals), so an existing circular area can be the starting draft. A circle that
 * cannot be drawn (invalid centre, non-positive or non-finite radius, more than half the globe) yields an EMPTY ring - the caller's
 * findings then say so, nothing throws.
 */
export function circleToRing(center, radiusMeters, segments = 64) {
    if (!isLatLng(center) || !Number.isFinite(radiusMeters) || radiusMeters <= 0 || radiusMeters >= Math.PI * EARTH_RADIUS_METERS)
        return [];
    const n = Number.isFinite(segments) ? Math.min(1000, Math.max(3, Math.floor(segments))) : 64;
    const angular = radiusMeters / EARTH_RADIUS_METERS;
    const lat1 = center.latitude * DEGREES;
    const lng1 = center.longitude * DEGREES;
    const ring = [];
    for (let i = 0; i < n; i++) {
        const bearing = (2 * Math.PI * i) / n;
        const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing));
        const lng2 = lng1 + Math.atan2(Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1), Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2));
        const lngDeg = ((((lng2 / DEGREES + 540) % 360) + 360) % 360) - 180;
        ring.push({ latitude: round7(lat2 / DEGREES), longitude: round7(lngDeg) });
    }
    return ring;
}
/** The vertex count of the outline a radius is stored as ({@link circleToRing}'s default); {@link serviceAreaAsCircle} recognises exactly this. */
export const SERVICE_AREA_CIRCLE_SEGMENTS = 64;
/** How far a vertex may sit from the mean radius and still be a circle: 0.5 %, but never less than 5 m (coordinates are rounded to ~1 cm). */
const CIRCLE_TOLERANCE_RATIO = 0.005;
const CIRCLE_TOLERANCE_FLOOR_METERS = 5;
/** How far a step between two neighbouring vertices may stray from 360 / 64 degrees as seen from the centre. */
const CIRCLE_STEP_TOLERANCE_DEGREES = 1.5;
/**
 * Whether a service-area geometry is a RADIUS (D616b): the owner picks "a centre and a radius" in the editor, which is stored as the
 * ordinary polygon `circleToRing(center, radius, 64)` - no new geometry kind, no migration - and recognised again here when it is
 * reopened. A real `circle` fence (the old seed area) is read as it is. A polygon (or a single-ring multipolygon) counts only when
 * it is ONE ring of exactly 64 vertices (the closing duplicate does not count) whose vertices all lie within 0.5 % (and at least 5 m)
 * of their mean great-circle distance from the ring's centre AND are evenly spread around it (each step 5.625 degrees, 1.5 off at
 * most), so a hand-drawn outline that merely has 64 points is a drawing, not a radius. Anything else yields `undefined`.
 *
 * The centre is the mean of the vertices as unit vectors on the sphere, brought back to the surface: for points evenly spread on a
 * small circle that mean lies exactly on the circle's axis (a mean of latitude/longitude would drift by the sphere's curvature, and
 * would break at the date line). The radius is the mean distance, rounded to the metre. Pure; never throws.
 */
export function serviceAreaAsCircle(geometry) {
    if (geometry.kind === 'circle') {
        const radiusMeters = Math.round(geometry.radiusMeters);
        return isLatLng(geometry.center) && Number.isFinite(radiusMeters) && radiusMeters > 0 ? { center: geometry.center, radiusMeters } : undefined;
    }
    const rings = geometry.kind === 'polygon' ? [geometry.ring] : geometry.kind === 'multipolygon' ? geometry.polygons : [];
    if (rings.length !== 1)
        return undefined;
    const ring = open(rings[0] ?? []);
    if (ring.length !== SERVICE_AREA_CIRCLE_SEGMENTS || !ring.every(isLatLng))
        return undefined;
    let x = 0;
    let y = 0;
    let z = 0;
    for (const v of ring) {
        const lat = v.latitude * DEGREES;
        const lng = v.longitude * DEGREES;
        x += Math.cos(lat) * Math.cos(lng);
        y += Math.cos(lat) * Math.sin(lng);
        z += Math.sin(lat);
    }
    const norm = Math.hypot(x, y, z);
    if (!(norm > 1e-9))
        return undefined; // the vertices cancel out (a ring around the globe), so there is no centre
    const center = { latitude: round7(Math.asin(z / norm) / DEGREES), longitude: round7(Math.atan2(y, x) / DEGREES) };
    if (!isLatLng(center))
        return undefined;
    const distances = ring.map((v) => haversineMeters(center, v));
    const mean = distances.reduce((sum, d) => sum + d, 0) / distances.length;
    const tolerance = Math.max(mean * CIRCLE_TOLERANCE_RATIO, CIRCLE_TOLERANCE_FLOOR_METERS);
    if (!(mean > 0) || mean >= Math.PI * EARTH_RADIUS_METERS / 2 || distances.some((d) => Math.abs(d - mean) > tolerance))
        return undefined;
    const lat0 = center.latitude * DEGREES;
    const lng0 = center.longitude * DEGREES;
    const bearings = ring.map((v) => {
        const lat = v.latitude * DEGREES;
        const dLng = v.longitude * DEGREES - lng0;
        return Math.atan2(Math.sin(dLng) * Math.cos(lat), Math.cos(lat0) * Math.sin(lat) - Math.sin(lat0) * Math.cos(lat) * Math.cos(dLng)) / DEGREES;
    });
    const step = 360 / SERVICE_AREA_CIRCLE_SEGMENTS;
    for (let i = 0; i < bearings.length; i++) {
        const delta = (((bearings[(i + 1) % bearings.length] - bearings[i]) % 360) + 360) % 360;
        if (Math.abs(delta - step) > CIRCLE_STEP_TOLERANCE_DEGREES)
            return undefined;
    }
    return { center, radiusMeters: Math.round(mean) };
}
/** The radius (m, great-circle) of the circle centred at `center` that passes through `point` - what dragging the radius handle to `point` means. */
export function radiusThroughPoint(center, point) {
    return isLatLng(center) && isLatLng(point) ? haversineMeters(center, point) : Number.NaN;
}
export const SERVICE_AREA_IMPORT_ISSUES = ['IMPORT_UNREADABLE', 'IMPORT_NO_POLYGON', 'IMPORT_HOLES_UNSUPPORTED', 'IMPORT_TOO_LARGE'];
/** Largest import accepted (characters of text; for a parsed object, its serialized length). 512 KB holds a city outline at survey precision. */
const IMPORT_MAX_CHARS = 512 * 1024;
const GEOJSON_MAX_DEPTH = 8;
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** A GeoJSON position `[lng, lat, ...]`. */
function geoJsonPosition(v) {
    if (!Array.isArray(v) || v.length < 2)
        return null;
    const [lng, lat] = v;
    return typeof lng === 'number' && typeof lat === 'number' && Number.isFinite(lng) && Number.isFinite(lat) ? { latitude: lat, longitude: lng } : null;
}
function geoJsonRing(v) {
    if (!Array.isArray(v))
        return null;
    const ring = [];
    for (const p of v) {
        const pos = geoJsonPosition(p);
        if (pos === null)
            return null;
        ring.push(pos);
    }
    return ring;
}
/** Outer rings of one GeoJSON Polygon `coordinates` array; any interior ring is a hole. */
function geoJsonPolygon(coordinates, out) {
    if (!Array.isArray(coordinates) || coordinates.length === 0)
        return 'IMPORT_UNREADABLE';
    const rings = [];
    for (const r of coordinates) {
        const ring = geoJsonRing(r);
        if (ring === null)
            return 'IMPORT_UNREADABLE';
        rings.push(ring);
    }
    if (rings.length > 1)
        return 'IMPORT_HOLES_UNSUPPORTED';
    out.push(rings[0]);
    return null;
}
function walkGeoJson(node, out, depth) {
    if (depth > GEOJSON_MAX_DEPTH || !isRecord(node) || typeof node.type !== 'string')
        return 'IMPORT_UNREADABLE';
    switch (node.type) {
        case 'Polygon':
            return geoJsonPolygon(node.coordinates, out);
        case 'MultiPolygon': {
            if (!Array.isArray(node.coordinates))
                return 'IMPORT_UNREADABLE';
            for (const polygon of node.coordinates) {
                const issue = geoJsonPolygon(polygon, out);
                if (issue !== null)
                    return issue;
            }
            return null;
        }
        case 'Feature':
            return node.geometry === null || node.geometry === undefined ? null : walkGeoJson(node.geometry, out, depth + 1);
        case 'FeatureCollection': {
            if (!Array.isArray(node.features))
                return 'IMPORT_UNREADABLE';
            for (const feature of node.features) {
                const issue = walkGeoJson(feature, out, depth + 1);
                if (issue !== null)
                    return issue;
            }
            return null;
        }
        case 'GeometryCollection': {
            if (!Array.isArray(node.geometries))
                return 'IMPORT_UNREADABLE';
            for (const g of node.geometries) {
                const issue = walkGeoJson(g, out, depth + 1);
                if (issue !== null)
                    return issue;
            }
            return null;
        }
        default:
            return null; // points, lines and unknown geometry types are not an area: ignored
    }
}
/**
 * Whether `value` would serialize (JSON) to more than `limit` characters, counted without serializing it: an iterative walk that
 * stops as soon as the running total passes the limit. Every node adds at least one character, so the walk is bounded by `limit`
 * even for a cyclic object or an array of absurd declared length (its length is added before its elements are visited).
 */
function serializedLengthExceeds(value, limit) {
    let total = 0;
    const stack = [value];
    while (stack.length > 0) {
        const node = stack.pop();
        if (typeof node === 'string')
            total += node.length + 2;
        else if (typeof node === 'number' || typeof node === 'boolean')
            total += String(node).length;
        else if (Array.isArray(node)) {
            total += node.length + 2;
            if (total > limit)
                return true;
            for (const item of node)
                stack.push(item);
        }
        else if (typeof node === 'object' && node !== null) {
            total += 2;
            for (const key of Object.keys(node)) {
                total += key.length + 3;
                if (total > limit)
                    return true;
                stack.push(node[key]);
            }
        }
        else
            total += 4; // null (anything else is rejected by the reader that follows)
        if (total > limit)
            return true;
    }
    return false;
}
function parseGeoJson(content) {
    let node = content;
    if (typeof content === 'string') {
        if (content.length > IMPORT_MAX_CHARS)
            return { issue: 'IMPORT_TOO_LARGE' };
        try {
            node = JSON.parse(content);
        }
        catch {
            return { issue: 'IMPORT_UNREADABLE' };
        }
    }
    else if (isRecord(content)) {
        if (serializedLengthExceeds(content, IMPORT_MAX_CHARS))
            return { issue: 'IMPORT_TOO_LARGE' };
    }
    const rings = [];
    const issue = walkGeoJson(node, rings, 0);
    return issue !== null ? { issue } : { rings };
}
/** XML name characters this reader accepts in a tag name (letters, digits, `_`, `.`, `-`, and `:` for a prefix). */
const isNameCode = (c) => (c >= 97 && c <= 122) || (c >= 65 && c <= 90) || (c >= 48 && c <= 57) || c === 95 || c === 46 || c === 45 || c === 58;
/** One KML coordinates body ("lng,lat[,alt]" tuples, whitespace separated, CDATA wrappers tolerated) as a ring, or null when unreadable. */
function kmlRing(body) {
    const ring = [];
    for (const tuple of body.replace(/<!\[CDATA\[|\]\]>/g, ' ').trim().split(/\s+/)) {
        if (tuple === '')
            continue;
        const parts = tuple.split(',');
        const lng = Number(parts[0]);
        const lat = Number(parts[1]);
        if (parts.length < 2 || parts[0] === '' || parts[1] === '' || !Number.isFinite(lng) || !Number.isFinite(lat))
            return null;
        ring.push({ latitude: lat, longitude: lng });
    }
    return ring;
}
/**
 * KML read in ONE forward pass over the text - no regular expression scans the input, because a lazy `[\s\S]*?` over unclosed input
 * (`<!--<!--...`, `<Polygon><Polygon>...`) retries from every start and is quadratic (80 KB of `<!--` took 5 s). Every `indexOf` here
 * starts at the cursor and the cursor only moves forward; a missing terminator ends the parse (IMPORT_UNREADABLE) instead of being
 * searched for again from the next candidate. Only the tags this reader needs are interpreted (local names, any prefix):
 * Polygon > outerBoundaryIs > coordinates (the first one) yields a ring; an innerBoundaryIs inside a Polygon is a hole; everything
 * else (Point, LineString, Placemark, ...) is skipped. A comment inside the coordinates is cut out of them.
 */
function parseKml(content) {
    if (typeof content !== 'string')
        return { issue: 'IMPORT_UNREADABLE' };
    if (content.length > IMPORT_MAX_CHARS)
        return { issue: 'IMPORT_TOO_LARGE' };
    const unreadable = { issue: 'IMPORT_UNREADABLE' };
    if (!content.includes('<'))
        return unreadable;
    const rings = [];
    let inPolygon = false;
    let inOuter = false;
    let ringBody = null; // the first coordinates of the current polygon's outer boundary
    let capturing = null; // pieces of the coordinates being read
    let piece = 0; // where the current piece started
    let cursor = 0;
    for (;;) {
        const lt = content.indexOf('<', cursor);
        if (lt === -1)
            break;
        const next = content.charCodeAt(lt + 1);
        if (next === 60) {
            let run = lt + 1; // `<<...`: every bracket but the last of the run is stray
            while (content.charCodeAt(run + 1) === 60)
                run++;
            cursor = run;
            continue;
        }
        if (next === 33 && content.startsWith('<!--', lt)) {
            const end = content.indexOf('-->', lt + 4);
            if (end === -1)
                return unreadable;
            if (capturing !== null) {
                capturing.push(content.slice(piece, lt));
                piece = end + 3;
            }
            cursor = end + 3;
            continue;
        }
        if (next === 33 && content.startsWith('<![CDATA[', lt)) {
            const end = content.indexOf(']]>', lt + 9);
            if (end === -1)
                return unreadable;
            cursor = end + 3;
            continue;
        }
        const closing = next === 47; // '/'
        const nameStart = lt + (closing ? 2 : 1);
        let nameEnd = nameStart;
        let localStart = nameStart; // after the last `:` (the namespace prefix is ignored)
        for (let code = content.charCodeAt(nameEnd); isNameCode(code); code = content.charCodeAt(++nameEnd))
            if (code === 58)
                localStart = nameEnd + 1;
        if (nameEnd === nameStart) {
            // `<?xml ... ?>`, `<!DOCTYPE ...>` or a stray bracket: skip it, but a declaration must end
            if (content[lt + 1] === '?' || content[lt + 1] === '!') {
                const end = content.indexOf('>', lt + 2);
                if (end === -1)
                    return unreadable;
                cursor = end + 1;
            }
            else
                cursor = lt + 1;
            continue;
        }
        const gt = content.indexOf('>', nameEnd);
        if (gt === -1)
            return unreadable;
        cursor = gt + 1;
        const width = nameEnd - localStart;
        if (width !== 7 && width !== 11 && width !== 15)
            continue; // none of Polygon / coordinates / outerBoundaryIs / innerBoundaryIs
        const local = content.slice(localStart, nameEnd);
        const selfClosing = content.charCodeAt(gt - 1) === 47;
        if (closing) {
            if (local === 'coordinates' && capturing !== null) {
                capturing.push(content.slice(piece, lt));
                ringBody ??= capturing.join(' ');
                capturing = null;
            }
            else if (local === 'outerBoundaryIs')
                inOuter = false;
            else if (local === 'Polygon' && inPolygon) {
                const ring = ringBody === null ? null : kmlRing(ringBody);
                if (ring === null)
                    return unreadable;
                rings.push(ring);
                inPolygon = false;
                inOuter = false;
                ringBody = null;
            }
            continue;
        }
        if (local === 'Polygon') {
            if (inPolygon || selfClosing)
                return unreadable; // a Polygon never nests in a Polygon, and an empty one has no outer boundary
            inPolygon = true;
            inOuter = false;
            ringBody = null;
        }
        else if (inPolygon && local === 'innerBoundaryIs')
            return { issue: 'IMPORT_HOLES_UNSUPPORTED' };
        else if (inPolygon && local === 'outerBoundaryIs')
            inOuter = !selfClosing;
        else if (inPolygon && inOuter && ringBody === null && capturing === null && local === 'coordinates' && !selfClosing) {
            capturing = [];
            piece = cursor;
        }
    }
    return inPolygon || capturing !== null ? unreadable : { rings };
}
/**
 * Parse GeoJSON (a string or a parsed object: Polygon, MultiPolygon, Feature, FeatureCollection - its polygon features union into a
 * multipolygon; coordinates are [lng, lat]) or KML (a string: every <Polygon>'s <outerBoundaryIs> <coordinates> "lng,lat[,alt]"
 * tuples; any <innerBoundaryIs> -> IMPORT_HOLES_UNSUPPORTED; single forward pass, no DOM and no regex scan over the input). Interior rings in GeoJSON ->
 * IMPORT_HOLES_UNSUPPORTED. Input over 512 KB of text -> IMPORT_TOO_LARGE. Non-polygon features (points, lines) are ignored; none
 * left -> IMPORT_NO_POLYGON. The result is normalized but NOT validated (call {@link serviceAreaFindings}).
 */
export function importServiceArea(input) {
    const parsed = input.format === 'geojson' ? parseGeoJson(input.content) : input.format === 'kml' ? parseKml(input.content) : { issue: 'IMPORT_UNREADABLE' };
    if ('issue' in parsed)
        return { ok: false, issue: parsed.issue };
    if (parsed.rings.length === 0)
        return { ok: false, issue: 'IMPORT_NO_POLYGON' };
    return { ok: true, geometry: normalizeServiceArea({ kind: 'multipolygon', polygons: parsed.rings }) };
}
/** Export as a GeoJSON Feature (Polygon/MultiPolygon, closed rings, [lng, lat]); the geometry is normalized first. */
export function serviceAreaToGeoJson(geometry) {
    const closed = serviceAreaRings(normalizeServiceArea(geometry)).map((ring) => {
        const positions = ring.map((v) => [v.longitude, v.latitude]);
        return ring.length > 0 ? [...positions, [ring[0].longitude, ring[0].latitude]] : positions;
    });
    return { type: 'Feature', properties: {}, geometry: closed.length === 1 ? { type: 'Polygon', coordinates: closed } : { type: 'MultiPolygon', coordinates: closed.map((ring) => [ring]) } };
}
/** Why an area was published (stable; the history shows its plain-language text). */
export const SERVICE_AREA_PUBLICATION_REASONS = ['SERVICE_AREA_FIRST_DRAWING', 'SERVICE_AREA_EXPANSION', 'SERVICE_AREA_REDUCTION', 'SERVICE_AREA_CORRECTION'];
/** Id of the operation fence a publication adds: `${marketId}:service-area:v${setVersion}`. */
export function serviceAreaFenceId(marketId, setVersion) {
    return `${marketId}:service-area:v${setVersion}`;
}
/**
 * The next WHOLE fence set when a service-area proposal is approved (pure):
 *  - every non-`operation` fence of `current` is carried unchanged;
 *  - the new `operation` fence (id = {@link serviceAreaFenceId}(marketId, nextVersion), version 1, priority 0, `schedule` when given -
 *    omitted = always open) starts at `effectiveFrom = max(requestedFrom ?? approvedAt, approvedAt)` - never back-dated, so a past
 *    ride's snapshot stays true (an unreadable `requestedFrom` is treated as absent: the store refuses it before it gets here);
 *  - each `operation` fence of `current` is CARRIED, clipped to `effectiveTo = min(its effectiveTo, newFrom)` (its `version` goes up
 *    by one when that changed its definition) - also when the publication is immediate, so a replica whose clock lags the approval
 *    still finds the old area in force and the market is never "not declared" in between. It is pruned only when it ended more
 *    than {@link SERVICE_AREA_SKEW_GRACE_MS} before `approvedAt`, or when it has not started before the new one (`effectiveFrom >= newFrom`).
 * Instants are compared by epoch (`Date.parse`), never as strings. Returns the fences and the effective start actually used.
 */
export function nextServiceAreaFences(input) {
    const approvedMs = Date.parse(input.approvedAt);
    const requestedMs = input.requestedFrom === undefined ? Number.NaN : Date.parse(input.requestedFrom);
    const useRequested = input.requestedFrom !== undefined && requestedMs > approvedMs;
    const effectiveFrom = useRequested ? input.requestedFrom : input.approvedAt;
    const fromMs = useRequested ? requestedMs : approvedMs;
    const fences = [];
    for (const fence of input.current) {
        if (fence.purpose !== 'operation') {
            fences.push(fence);
            continue;
        }
        const ownEnd = fence.effectiveTo === undefined ? Number.POSITIVE_INFINITY : Date.parse(fence.effectiveTo);
        if (ownEnd < approvedMs - SERVICE_AREA_SKEW_GRACE_MS)
            continue; // over for longer than any replica's clock can lag
        if (Date.parse(fence.effectiveFrom) >= fromMs)
            continue; // never started before the new area does
        if (fence.effectiveTo !== undefined && ownEnd <= fromMs)
            fences.push(fence); // already ends first: definition unchanged
        else
            fences.push({ ...fence, effectiveTo: effectiveFrom, version: fence.version + 1 });
    }
    fences.push({
        id: serviceAreaFenceId(input.marketId, input.nextVersion),
        marketId: input.marketId,
        purpose: 'operation',
        geometry: normalizeServiceArea(input.geometry),
        priority: 0,
        version: 1,
        effectiveFrom,
        ...(input.schedule !== undefined && input.schedule.windows.length > 0 ? { schedule: input.schedule } : {}),
    });
    return { fences, effectiveFrom };
}
/** Look-ahead of {@link nextServiceAreaOpening}: one week covers every weekly schedule. */
const OPENING_LOOKAHEAD_MS = 7 * 24 * 60 * 60 * 1000;
const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;
const WEEKDAYS_BY_UTC_DAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const offsetFormatters = new Map();
/** The zone's offset from UTC (milliseconds, east positive) at a UTC instant. One cached formatter per zone. */
function zoneOffsetMs(ms, timeZone) {
    let formatter = offsetFormatters.get(timeZone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' });
        if (offsetFormatters.size > 64)
            offsetFormatters.clear();
        offsetFormatters.set(timeZone, formatter);
    }
    const wholeSeconds = Math.floor(ms / 1000) * 1000;
    let year = 1970;
    let month = 1;
    let day = 1;
    let hour = 0;
    let minute = 0;
    let second = 0;
    for (const part of formatter.formatToParts(new Date(wholeSeconds))) {
        const value = Number(part.value);
        if (part.type === 'year')
            year = value;
        else if (part.type === 'month')
            month = value;
        else if (part.type === 'day')
            day = value;
        else if (part.type === 'hour')
            hour = value === 24 ? 0 : value;
        else if (part.type === 'minute')
            minute = value;
        else if (part.type === 'second')
            second = value;
    }
    return Date.UTC(year, month - 1, day, hour, minute, second) - wholeSeconds;
}
/** The instants at which the zone's clock shows this local time on a calendar day (both, if a change of the clock repeats it; the change itself, if it skips it). */
function localTimeInstants(dayUtcMs, minutesOfDay, timeZone) {
    const naive = dayUtcMs + minutesOfDay * MINUTE_MS; // the wall-clock reading as if it were UTC
    const found = new Set();
    // The offsets in force around this wall-clock time: those of the day before and the day after bound every offset the day can show.
    for (const probe of [naive - DAY_MS, naive + DAY_MS]) {
        const instant = naive - zoneOffsetMs(probe, timeZone);
        if (zoneOffsetMs(instant, timeZone) === naive - instant)
            found.add(instant);
    }
    if (found.size > 0)
        return [...found];
    // The wall-clock time does not exist on that day (the clock jumped over it): the window opens when the clock jumps, which is the
    // instant the offset changes. Find it by bisection between the two probes (minute resolution).
    let low = naive - DAY_MS - zoneOffsetMs(naive - DAY_MS, timeZone);
    let high = naive + DAY_MS - zoneOffsetMs(naive + DAY_MS, timeZone);
    if (low > high)
        [low, high] = [high, low];
    const offsetLow = zoneOffsetMs(low, timeZone);
    while (high - low > MINUTE_MS) {
        const mid = Math.floor((low + (high - low) / 2) / MINUTE_MS) * MINUTE_MS;
        if (mid <= low)
            break;
        if (zoneOffsetMs(mid, timeZone) === offsetLow)
            low = mid;
        else
            high = mid;
    }
    return [high];
}
/**
 * The next UTC instant (minute resolution, at most 7 days ahead) at which the market's service area OPENS - the first minute after
 * `ctx.at` at which an `operation` fence is in effect AND its schedule is active, having been closed the minute before - or
 * `undefined` when the area never closes-and-reopens in that week (already open all week, or no usable fence). Used to tell a
 * refused rider when the city serves again. Pure; `timeZone` is the market's IANA zone.
 *
 * It does not scan the week minute by minute: an area can only open at a window start (on each of the next local days, converted
 * to UTC - correct across a change of the clock) or at the instant a fence takes effect, so those candidates are sorted and the first
 * that is open (and was closed the minute before) wins. With `ctx.points` (the refused points) only the fences that contain one of
 * them take part, and "open" means every point is covered - a fence elsewhere in the market must not promise a reopening.
 */
export function nextServiceAreaOpening(fences, ctx) {
    const atMs = Date.parse(ctx.at);
    if (Number.isNaN(atMs))
        return undefined;
    // D616 round 3: an unreadable market zone promises no opening (and never throws on the quote path).
    if (!isValidTimeZone(ctx.timeZone))
        return undefined;
    const startMs = Math.floor(atMs / MINUTE_MS) * MINUTE_MS;
    const endMs = startMs + OPENING_LOOKAHEAD_MS;
    const points = ctx.points;
    const operation = fences.filter((f) => f.purpose === 'operation' && f.marketId === ctx.marketId && validateGeometry(f.geometry) === null && (points === undefined || points.some((p) => isLatLng(p.location) && contains(f.geometry, p.location))));
    if (operation.length === 0)
        return undefined;
    const openAt = (ms) => {
        const at = new Date(ms).toISOString();
        if (points !== undefined)
            return evaluateServiceArea(operation, points, { marketId: ctx.marketId, at, timeZone: ctx.timeZone }).reasonCode === 'SERVICE_AREA_COVERED';
        return operation.some((f) => isActiveAt(f, { at, timeZone: ctx.timeZone, market: ctx.marketId }));
    };
    const candidates = new Set();
    const consider = (ms) => {
        const minute = Math.ceil(ms / MINUTE_MS) * MINUTE_MS;
        if (minute > startMs && minute <= endMs)
            candidates.add(minute);
    };
    for (const f of operation) {
        const from = Date.parse(f.effectiveFrom);
        if (!Number.isNaN(from))
            consider(from);
    }
    // The local calendar day of `at`, then the next 8: a 7-day look-ahead touches at most 9 local days.
    const local = atMs + zoneOffsetMs(atMs, ctx.timeZone);
    const firstDay = Math.floor(local / DAY_MS) * DAY_MS;
    const windows = operation.flatMap((f) => (f.schedule?.windows ?? []).filter((w) => w.days.length > 0));
    if (windows.length > 0) {
        const starts = new Map();
        for (const w of windows) {
            const [hh, mm] = w.start.split(':');
            const minutes = Number(hh) * 60 + Number(mm);
            if (Number.isFinite(minutes))
                starts.set(w.start, minutes);
        }
        for (let i = 0; i <= 8; i++) {
            const dayUtcMs = firstDay + i * DAY_MS;
            const weekday = WEEKDAYS_BY_UTC_DAY[new Date(dayUtcMs).getUTCDay()];
            const todays = new Set();
            for (const w of windows)
                if (weekday !== undefined && w.days.includes(weekday))
                    todays.add(w.start);
            for (const start of todays) {
                const minutes = starts.get(start);
                if (minutes === undefined)
                    continue;
                for (const instant of localTimeInstants(dayUtcMs, minutes, ctx.timeZone))
                    consider(instant);
            }
        }
    }
    for (const candidate of [...candidates].sort((a, b) => a - b)) {
        if (openAt(candidate) && !openAt(candidate - MINUTE_MS))
            return new Date(candidate).toISOString();
    }
    return undefined;
}
//# sourceMappingURL=service-area-editor.js.map